/**
 * grainLogic.ts
 * Pure business-logic functions for the KPI One Pager grain configuration.
 *
 * Converted from the HTML click-dummy (navigator-analytics-hub-v8.html).
 * All functions are pure: no DOM access, no global state, no side effects.
 */

import {
	GrainKey,
	GrainDimension,
	GroupedRow,
	PagerColumn,
	TableSortState,
	GRAIN_DIMENSIONS,
	MAX_GRAIN_COLUMNS,
	DEFAULT_GRAIN_SELECTIONS,
} from '../constants/onePagerConstants';

// ─── Types ────────────────────────────────────────────────────────────────────

/** A geographic location record with dimension attributes */
export interface GeoLocation {
	rg: string; // Region / Unit
	mk: string; // Market / POD
	lc: string; // Location name/id
	lt: string; // Location Type
}

/** The applied filter selections (arrays of selected values per dimension) */
export interface AppliedFilters {
	rg: string[];
	mk: string[];
	lc: string[];
	lt: string[];
	yr: string[];
	pd: string[];
	wk: string[];
	dy: string[];
}

/** Immutable grain configuration state */
export interface GrainConfigState {
	/** Working copy of grain selections (may contain empty strings for unfilled slots) */
	pendingGrains: string[];
	/** Number of visible dropdown slots */
	grainSlotCount: number;
	/** Committed grain selections that drive the table */
	grainSelections: GrainKey[];
	/** Whether user has manually applied a config (suppresses auto drill-down) */
	manualOverride: boolean;
}

/** Parameters for buildGrainRows */
export interface BuildGrainRowsParams {
	/** All location records to group */
	locations: GeoLocation[];
	/** Currently committed grain selections */
	grainSelections: GrainKey[];
	/** Applied filter state (used to resolve time-dimension grain values) */
	appliedFilters: AppliedFilters;
	/** KPI column definitions (determines which values to aggregate) */
	columns: PagerColumn[];
	/**
	 * Callback to compute a single location's value for a given column.
	 * Decouples grain logic from the value engine.
	 */
	getLocationValue: (locationKey: string, columnId: string) => number;
}

// ─── Functions ────────────────────────────────────────────────────────────────

/**
 * Get the human-readable label for a grain key.
 *
 * @param key - The grain dimension key (e.g. 'rg', 'mk')
 * @param dimensions - Optional custom dimensions array; defaults to GRAIN_DIMENSIONS
 * @returns The label string, or the key itself if not found
 */
export const grainLabel = (
	key: GrainKey,
	dimensions: GrainDimension[] = GRAIN_DIMENSIONS,
): string => {
	const dim = dimensions.find((d) => d.key === key);
	return dim?.label ?? key;
};

/**
 * Handle a pending grain dropdown change at a specific slot index.
 * Enforces uniqueness: if the selected value already exists in another slot, that slot is cleared.
 *
 * @param state - Current grain config state
 * @param idx - The slot index being changed
 * @param val - The new value (grain key string, or '' to clear)
 * @returns New state with the change applied
 */
export const onGrainPendingChange = (
	state: GrainConfigState,
	idx: number,
	val: string,
): GrainConfigState => {
	let pendingGrains = [...state.pendingGrains];

	if (val) {
		// Clear any other slot that already has this value (enforce uniqueness)
		pendingGrains = pendingGrains.map((v) => (v === val ? '' : v));
	}

	// Ensure array is large enough for the index
	while (pendingGrains.length <= idx) {
		pendingGrains.push('');
	}

	pendingGrains[idx] = val;

	return {
		...state,
		pendingGrains,
	};
};

/**
 * Add a new empty grain slot.
 *
 * @param state - Current grain config state
 * @param maxSlots - Maximum allowed slots (defaults to MAX_GRAIN_COLUMNS)
 * @returns New state with an additional slot, or unchanged if at max
 */
export const addGrainSlot = (
	state: GrainConfigState,
	maxSlots: number = MAX_GRAIN_COLUMNS,
): GrainConfigState => {
	if (state.grainSlotCount >= maxSlots) {
		return state;
	}

	const grainSlotCount = state.grainSlotCount + 1;
	const pendingGrains = [...state.pendingGrains];

	while (pendingGrains.length < grainSlotCount) {
		pendingGrains.push('');
	}

	return {
		...state,
		pendingGrains,
		grainSlotCount,
	};
};

/**
 * Remove a grain slot at a specific index.
 *
 * @param state - Current grain config state
 * @param idx - The slot index to remove
 * @returns New state with the slot removed, or unchanged if only 1 slot remains
 */
export const removeGrainSlot = (
	state: GrainConfigState,
	idx: number,
): GrainConfigState => {
	if (state.grainSlotCount <= 1) {
		return state;
	}

	const pendingGrains = [...state.pendingGrains];
	pendingGrains.splice(idx, 1);

	const grainSlotCount = state.grainSlotCount - 1;

	// Ensure pending array matches slot count
	while (pendingGrains.length < grainSlotCount) {
		pendingGrains.push('');
	}

	return {
		...state,
		pendingGrains,
		grainSlotCount,
	};
};

/**
 * Commit pending grains as the active grain configuration.
 * Filters out empty slots. Returns null if no valid selections exist.
 *
 * @param state - Current grain config state
 * @returns New committed state, or null if no valid grains selected
 */
export const applyGrainConfig = (
	state: GrainConfigState,
): GrainConfigState | null => {
	const committed = state.pendingGrains.filter((v) => v !== '') as GrainKey[];

	if (committed.length === 0) {
		return null; // Caller should show validation message
	}

	return {
		pendingGrains: [...committed],
		grainSlotCount: Math.max(state.grainSlotCount, committed.length),
		grainSelections: committed,
		manualOverride: true,
	};
};

/**
 * Reset grain configuration to auto drill-down mode.
 *
 * @param defaultSelections - The default grain selections to restore (defaults to DEFAULT_GRAIN_SELECTIONS)
 * @returns Fresh state with manualOverride disabled
 */
export const resetGrainConfig = (
	defaultSelections: GrainKey[] = DEFAULT_GRAIN_SELECTIONS,
): GrainConfigState => {
	return {
		pendingGrains: [...defaultSelections],
		grainSlotCount: defaultSelections.length,
		grainSelections: [...defaultSelections],
		manualOverride: false,
	};
};

/**
 * Filter locations by geographic dimension selections.
 * A location passes if it matches ALL non-empty filter arrays.
 * An empty filter array means "all values accepted" for that dimension.
 *
 * @param locations - All available location records
 * @param filters - The applied filter selections
 * @returns Filtered subset of locations
 */
export const filterLocationsByGeo = (
	locations: GeoLocation[],
	filters: AppliedFilters,
): GeoLocation[] => {
	return locations.filter(
		(g) =>
			(filters.rg.length === 0 || filters.rg.includes(g.rg)) &&
			(filters.mk.length === 0 || filters.mk.includes(g.mk)) &&
			(filters.lc.length === 0 || filters.lc.includes(g.lc)) &&
			(filters.lt.length === 0 || filters.lt.includes(g.lt)),
	);
};

/**
 * Build grouped rows by grain selections.
 * Groups locations by their grain dimension values, then aggregates KPI values
 * for each group using the column's aggregation method (sum or avg).
 *
 * @param params - Configuration for building grain rows
 * @returns Array of grouped rows with grain values and aggregated KPI values
 */
export const buildGrainRows = (params: BuildGrainRowsParams): GroupedRow[] => {
	const { locations, grainSelections, appliedFilters, columns, getLocationValue } = params;

	if (locations.length === 0 || grainSelections.length === 0) {
		return [];
	}

	// Group locations by their grain dimension composite key
	const groups = new Map<
		string,
		{ members: GeoLocation[]; grainVals: Record<string, string> }
	>();

	locations.forEach((loc) => {
		const parts: string[] = [];

		grainSelections.forEach((key) => {
			switch (key) {
				case 'rg':
					parts.push(loc.rg);
					break;
				case 'mk':
					parts.push(loc.mk);
					break;
				case 'lc':
					parts.push(loc.lc);
					break;
				case 'lt':
					parts.push(loc.lt);
					break;
				case 'yr':
					parts.push(
						appliedFilters.yr.length > 0 ? appliedFilters.yr[0] : 'All',
					);
					break;
				case 'pd':
					parts.push(
						appliedFilters.pd.length > 0 ? appliedFilters.pd[0] : 'All',
					);
					break;
				case 'wk':
					parts.push(
						appliedFilters.wk.length > 0 ? appliedFilters.wk[0] : 'All',
					);
					break;
				case 'dt':
					parts.push(
						appliedFilters.dy.length > 0 ? appliedFilters.dy[0] : 'All',
					);
					break;
				default:
					parts.push('');
			}
		});

		const compositeKey = parts.join('|||');

		if (!groups.has(compositeKey)) {
			const grainVals: Record<string, string> = {};
			grainSelections.forEach((k, i) => {
				grainVals[k] = parts[i];
			});
			groups.set(compositeKey, { members: [], grainVals });
		}

		groups.get(compositeKey)!.members.push(loc);
	});

	// Aggregate KPI values for each group
	const rows: GroupedRow[] = [];

	groups.forEach((group) => {
		const vals: Record<string, number> = {};

		columns.forEach((col) => {
			let sum = 0;
			group.members.forEach((loc) => {
				sum += getLocationValue(loc.lc, col.id);
			});

			vals[col.id] = col.gAgg === 'sum' ? sum : sum / group.members.length;
		});

		rows.push({
			grainVals: group.grainVals,
			vals,
			memberCount: group.members.length,
		});
	});

	return rows;
};

/**
 * Grains whose values are numeric strings. These must be compared numerically,
 * otherwise localeCompare orders weeks as 1, 10, 11, 2 instead of 1, 2, 10, 11.
 */
const NUMERIC_GRAINS: ReadonlySet<string> = new Set(['yr', 'pd', 'wk', 'dt']);

/**
 * Sort grouped rows by the combined grain hierarchy ('geo'), a single grain
 * dimension (e.g. 'rg'), or a KPI column value.
 *
 * @param rows - The grouped rows to sort
 * @param sort - The current sort state (col + direction)
 * @param grainSelections - Current grain selections (used for geo label sorting
 *   and to identify whether `col` refers to a grain rather than a KPI)
 * @returns A new sorted array (does not mutate input)
 */
export const sortGrainRows = (
	rows: GroupedRow[],
	sort: TableSortState,
	grainSelections: GrainKey[],
): GroupedRow[] => {
	const { col, dir } = sort;

	// A grain column sorts on grainVals; anything else falls through to vals.
	// Checked against grainSelections because GrainKey is a compile-time-only
	// type with no runtime list to test membership against.
	const isGrainCol = grainSelections.includes(col as GrainKey);

	return [...rows].sort((a, b) => {
		if (col === 'geo') {
			// Sort by concatenated grain labels
			const aLabel = grainSelections
				.map((k) => a.grainVals[k] || '')
				.join(' ');
			const bLabel = grainSelections
				.map((k) => b.grainVals[k] || '')
				.join(' ');
			return dir === 'asc'
				? aLabel.localeCompare(bLabel)
				: bLabel.localeCompare(aLabel);
		}

		if (isGrainCol) {
			// Sort by a single grain dimension
			const aRaw = a.grainVals[col] || '';
			const bRaw = b.grainVals[col] || '';

			if (NUMERIC_GRAINS.has(col)) {
				const aNum = Number(aRaw);
				const bNum = Number(bRaw);
				// Fall back to string compare if either value isn't parseable
				if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
					return dir === 'asc' ? aNum - bNum : bNum - aNum;
				}
			}

			return dir === 'asc'
				? aRaw.localeCompare(bRaw)
				: bRaw.localeCompare(aRaw);
		}

		// Sort by KPI column value
		const aVal = a.vals[col] ?? 0;
		const bVal = b.vals[col] ?? 0;
		return dir === 'asc' ? aVal - bVal : bVal - aVal;
	});
};

